import React, { useMemo, useState } from 'react';
import { rowFor } from '../utils/rtl';
import { ActivityIndicator, Platform, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import type { WorkspaceAction, WorkspaceTable, WorkspaceTableColumn, WorkspaceTableRow } from '../services/workspace-assistant.types';
import { useLocale } from '../contexts/LocaleContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { exportFileName, formatWorkspaceCell, statusTone, tableGrid, toCsv, toTsv } from '../utils/workspaceExport';
import { saveTextFile } from '../utils/saveTextFile';
import { chartData } from '../utils/workspaceChart';
import { WorkspaceChart } from './WorkspaceChart';
import { InsetModal } from './InsetModal';
import { colors, spacing, typography } from '../theme';

const PREVIEW = 8;
/** Rows shown under a reply; the rest are in the full table. */
const INLINE = 3;
const WIDTH: Record<string, number> = { number: 84, money: 104, date: 128, hours: 96, status: 108, phone: 136, text: 156 };
const WIDE = new Set(['message', 'body', 'description', 'error', 'outcome']);
const widthOf = (c: WorkspaceTableColumn) => (WIDE.has(c.key) ? 230 : WIDTH[c.kind || 'text']);
const ICONS: Record<string, keyof typeof Ionicons.glyphMap> = {
  conversations: 'chatbubbles-outline', messages: 'megaphone-outline', contacts: 'people-outline', products: 'cube-outline', orders: 'receipt-outline',
  quotations: 'document-text-outline', vendors: 'storefront-outline', analytics: 'stats-chart-outline', templates: 'chatbox-ellipses-outline', product_coverage: 'git-network-outline',
};

type Props = {
  table: WorkspaceTable; busy: boolean; loadingMore: boolean; canCall: boolean;
  onOpen: (action: WorkspaceAction) => void; onChat: (row: WorkspaceTableRow) => void; onCall: (row: WorkspaceTableRow) => void; onMore: () => void;
  /** The whole table in its own window (every column, filter, paging); otherwise a preview that fits the chat. */
  full?: boolean; onClose?: () => void;
};

/**
 * One lookup's rows under a reply: a few rows and the columns that fit the chat, so the reply stays
 * readable; "Open table" shows it all (filter, page, every column). Open a row, chat / call a customer,
 * copy or download either way.
 */
export function WorkspaceDataTable(props: Props) {
  const { table, busy, loadingMore, canCall, onOpen, onChat, onCall, onMore, full = false, onClose } = props;
  const styles = useThemedStyles(getStyles);
  const { t, isRTL, locale } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState('');
  const [copied, setCopied] = useState(false);
  const chart = useMemo(() => chartData(table), [table]);
  const [showChart, setShowChart] = useState(true);
  const number = (n: number) => formatWorkspaceCell(n, 'number', locale);
  const row = { flexDirection: rowFor(isRTL) };
  const align = { textAlign: isRTL ? 'right' as const : 'left' as const };
  const format = (value: any, column: WorkspaceTableColumn) => formatWorkspaceCell(value, column.kind, locale);
  const matching = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? table.rows.filter(r => Object.values(r.cells).some(v => v != null && String(v).toLowerCase().includes(q))) : table.rows;
  }, [filter, table.rows]);
  const visible = !full ? matching.slice(0, INLINE) : expanded || filter ? matching : matching.slice(0, PREVIEW);
  const quick = table.rows.some(r => r.contactId);
  const [width, setWidth] = useState(0);
  const [opened, setOpened] = useState(false);
  // The preview's columns: as many leading columns as fit the chat's width (always the first).
  const columns = useMemo(() => {
    if (full) return table.columns;
    if (!width) return table.columns.slice(0, 2);
    let used = quick ? 70 : 0;
    const fit: WorkspaceTableColumn[] = [];
    for (const c of table.columns) { used += widthOf(c); if (fit.length && used > width) break; fit.push(c); }
    return fit;
  }, [full, width, quick, table.columns]);
  const hiddenColumns = table.columns.length - columns.length;
  const more = !full && (matching.length > INLINE || hiddenColumns > 0 || !!table.canLoadMore || (table.total ?? 0) > table.rows.length);
  const colStyle = (c: WorkspaceTableColumn) => (full ? { width: widthOf(c) } : { flexGrow: 1, flexShrink: 1, flexBasis: widthOf(c), minWidth: 0 });
  const total = table.total ?? table.rows.length;
  const grid = () => tableGrid(table, format);
  const copy = async () => { try { await Clipboard.setStringAsync(toTsv(grid())); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard unavailable */ } };
  const download = () => void saveTextFile(toCsv(grid()), exportFileName(table.title, 'csv'), 'text/csv');

  const cell = (r: WorkspaceTableRow, c: WorkspaceTableColumn, first: boolean) => {
    const value = r.cells[c.key];
    const text = format(value, c);
    if (c.kind === 'status' && text) {
      const tone = statusTone(value);
      return <View style={[styles.chip, styles[`chip_${tone}`]]}><Text style={[styles.chipText, styles[`chipText_${tone}`]]} numberOfLines={1}>{text}</Text></View>;
    }
    const ltr = c.kind === 'phone' || c.kind === 'number' || c.kind === 'money';
    return <Text selectable numberOfLines={WIDE.has(c.key) ? 3 : 2}
      style={[styles.cellText, align, ltr && { writingDirection: 'ltr' }, first && r.open && styles.link, (c.kind === 'number' || c.kind === 'money') && styles.number]}>{text || '—'}</Text>;
  };

  const Rows = full ? ScrollView : View;
  const rowsProps = full ? { horizontal: true, showsHorizontalScrollIndicator: true, contentContainerStyle: { flexDirection: rowFor(isRTL), flexGrow: 1 } } : {};
  return <View style={[styles.card, full && styles.fullCard]} onLayout={full ? undefined : e => setWidth(Math.floor(e.nativeEvent.layout.width))}>
    {/* Title, count and tools */}
    <View style={[styles.head, row]}>
      <Ionicons name={ICONS[table.module] || 'list-outline'} size={16} color={colors.primary as string} />
      <Text style={[styles.title, align]} numberOfLines={1}>{table.title}</Text>
      <View style={styles.count}><Text style={styles.countText}>{total.toLocaleString(locale)}</Text></View>
      <View style={{ flex: 1 }} />
      {chart && <TouchableOpacity accessibilityRole="button" accessibilityState={{ selected: showChart }} accessibilityLabel={t(showChart ? 'workspaceAssistant.table.hideChart' : 'workspaceAssistant.table.chart')}
        {...tip(t(showChart ? 'workspaceAssistant.table.hideChart' : 'workspaceAssistant.table.chart'))} style={[styles.tool, showChart && styles.toolOn]} onPress={() => setShowChart(v => !v)}>
        <Ionicons name={chart.type === 'pie' ? 'pie-chart-outline' : 'bar-chart-outline'} size={15} color={(showChart ? colors.primary : colors.text.secondary) as string} />
      </TouchableOpacity>}
      <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.table.copy')} {...tip(t('workspaceAssistant.table.copy'))} style={styles.tool} onPress={copy}>
        <Ionicons name={copied ? 'checkmark' : 'copy-outline'} size={15} color={colors.text.secondary as string} />
      </TouchableOpacity>
      <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.table.csv')} {...tip(t('workspaceAssistant.table.csv'))} style={styles.tool} onPress={download}>
        <Ionicons name="download-outline" size={16} color={colors.text.secondary as string} />
      </TouchableOpacity>
      {table.screen && <TouchableOpacity accessibilityRole="button" accessibilityLabel={table.screen.label} {...tip(table.screen.label)} style={styles.tool} onPress={() => onOpen(table.screen!)}>
        <Ionicons name="open-outline" size={16} color={colors.primary as string} />
      </TouchableOpacity>}
      {onClose && <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.table.close')} {...tip(t('workspaceAssistant.table.close'))} style={styles.tool} onPress={onClose}>
        <Ionicons name="close" size={18} color={colors.text.secondary as string} />
      </TouchableOpacity>}
    </View>

    {!!table.stats?.length && <View style={[styles.stats, row]}>
      {table.stats.map((s, i) => <View key={i} style={[styles.stat, s.tone && styles[`stat_${s.tone}`]]}>
        <Text style={[styles.statValue, s.tone && styles[`statValue_${s.tone}`]]}>{typeof s.value === 'number' ? s.value.toLocaleString(locale) : s.value}</Text>
        <Text style={styles.statLabel} numberOfLines={1}>{s.label}</Text>
      </View>)}
    </View>}

    {chart && showChart && <WorkspaceChart data={chart} isRTL={isRTL} format={number} />}

    {full && table.rows.length > PREVIEW && <View style={[styles.filter, row]}>
      <Ionicons name="search" size={13} color={colors.text.secondary as string} />
      <TextInput value={filter} onChangeText={setFilter} placeholder={t('workspaceAssistant.table.filter')} placeholderTextColor={colors.text.secondary as string}
        style={[styles.filterInput, align, Platform.OS === 'web' && ({ outlineStyle: 'none' } as any)]} />
    </View>}

    <Rows {...rowsProps} style={full ? styles.fullRows : undefined}>
      <View style={{ flexGrow: 1 }}>
        <View style={[styles.tr, styles.th, row]}>
          {quick && <View style={styles.quickCol} />}
          {columns.map(c => <View key={c.key} style={[styles.td, colStyle(c)]}><Text style={[styles.thText, align]} numberOfLines={1}>{c.label}</Text></View>)}
        </View>
        {visible.map((r, i) => <TouchableOpacity key={r.id} activeOpacity={r.open ? 0.6 : 1} disabled={!r.open} accessibilityRole={r.open ? 'button' : undefined}
          accessibilityLabel={r.open ? `${t('workspaceAssistant.table.open')} ${r.open.label}` : undefined} onPress={() => r.open && onOpen(r.open)}
          style={[styles.tr, row, i % 2 === 1 && styles.stripe]}>
          {quick && <View style={[styles.quickCol, row]}>
            {r.contactId && <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.table.chat')} {...tip(t('workspaceAssistant.table.chat'))} style={styles.quick} onPress={() => onChat(r)}>
              <Ionicons name="chatbubble-ellipses-outline" size={15} color={colors.primary as string} />
            </TouchableOpacity>}
            {r.contactId && canCall && <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.table.call')} {...tip(t('workspaceAssistant.table.call'))} style={styles.quick} onPress={() => onCall(r)}>
              <Ionicons name="call-outline" size={15} color={colors.primary as string} />
            </TouchableOpacity>}
          </View>}
          {columns.map((c, ci) => <View key={c.key} style={[styles.td, colStyle(c)]}>{cell(r, c, ci === 0)}</View>)}
        </TouchableOpacity>)}
        {!visible.length && <Text style={[styles.empty, align]}>{t('workspaceAssistant.table.noMatch')}</Text>}
      </View>
    </Rows>

    {/* Preview: n of total, hidden columns, open the full table */}
    {!full && <View style={[styles.foot, row]}>
      <Text style={styles.footText}>{t('workspaceAssistant.table.showing', { shown: String(visible.length), total: total.toLocaleString(locale) })}</Text>
      {hiddenColumns > 0 && <Text style={styles.footText}>· {t('workspaceAssistant.table.moreColumns', { count: String(hiddenColumns) })}</Text>}
      <View style={{ flex: 1 }} />
      {more && <TouchableOpacity accessibilityRole="button" style={[styles.footButton, styles.more, row]} onPress={() => setOpened(true)}>
        <Ionicons name="expand-outline" size={14} color={colors.primary as string} />
        <Text style={styles.footLink}>{t('workspaceAssistant.table.expand')}</Text>
      </TouchableOpacity>}
    </View>}
    {opened && <InsetModal visible transparent animationType="fade" onRequestClose={() => setOpened(false)}>
      <View style={styles.backdrop}>
        <TouchableOpacity accessible={false} activeOpacity={1} style={StyleSheet.absoluteFill} onPress={() => setOpened(false)} />
        <View style={styles.window}>
          <ScrollView contentContainerStyle={styles.windowBody}>
            <WorkspaceDataTable {...props} full onClose={() => setOpened(false)} />
          </ScrollView>
        </View>
      </View>
    </InsetModal>}

    {/* Showing n of total · show all · load more from the server */}
    {full && <View style={[styles.foot, row]}>
      <Text style={styles.footText}>{t('workspaceAssistant.table.showing', { shown: String(visible.length), total: total.toLocaleString(locale) })}</Text>
      <View style={{ flex: 1 }} />
      {!filter && matching.length > PREVIEW && <TouchableOpacity accessibilityRole="button" style={styles.footButton} onPress={() => setExpanded(v => !v)}>
        <Text style={styles.footLink}>{expanded ? t('workspaceAssistant.table.showLess') : t('workspaceAssistant.table.showAll', { count: String(matching.length) })}</Text>
      </TouchableOpacity>}
      {table.canLoadMore && <TouchableOpacity accessibilityRole="button" disabled={busy || loadingMore} style={[styles.footButton, styles.more, row, (busy || loadingMore) && { opacity: 0.5 }]} onPress={() => { setExpanded(true); onMore(); }}>
        {loadingMore ? <ActivityIndicator size="small" color={colors.primary as string} /> : <Ionicons name="add" size={14} color={colors.primary as string} />}
        <Text style={styles.footLink}>{t('workspaceAssistant.table.loadMore')}</Text>
      </TouchableOpacity>}
    </View>}
  </View>;
}

/** Hover text on the web. */
const tip = (title: string) => (Platform.OS === 'web' ? { title } as any : {});

const getStyles = () => StyleSheet.create({
  fullCard: { borderWidth: 0, borderRadius: 0 },
  fullRows: { flexGrow: 0 },
  backdrop: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, backgroundColor: 'rgba(0,0,0,0.45)' },
  window: { width: '100%', maxWidth: 1100, maxHeight: '90%', borderRadius: 14, overflow: 'hidden', backgroundColor: colors.background.surface },
  windowBody: { flexGrow: 1 },
  card: { borderWidth: 1, borderColor: colors.border, borderRadius: 12, overflow: 'hidden', backgroundColor: colors.background.surface },
  head: { alignItems: 'center', gap: 6, paddingStart: 12, paddingEnd: 4, paddingVertical: 6 },
  title: { fontFamily: typography.fontFamily.semibold, fontSize: 13, color: colors.text.primary, flexShrink: 1 },
  count: { paddingHorizontal: 7, paddingVertical: 1, borderRadius: 999, backgroundColor: colors.primaryTransparent },
  countText: { fontSize: 11, fontFamily: typography.fontFamily.semibold, color: colors.primary },
  tool: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center' },
  toolOn: { backgroundColor: colors.primaryTransparent },
  stats: { flexWrap: 'wrap', gap: 6, paddingHorizontal: 10, paddingBottom: 8 },
  stat: { minWidth: 76, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 10, backgroundColor: colors.background.secondary },
  stat_good: { backgroundColor: String(colors.status.success) + '18' },
  stat_warn: { backgroundColor: String(colors.status.warning) + '1f' },
  stat_bad: { backgroundColor: String(colors.status.error) + '18' },
  statValue: { fontSize: 16, lineHeight: 21, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  statValue_good: { color: colors.status.success },
  statValue_warn: { color: colors.status.warning },
  statValue_bad: { color: colors.status.error },
  statLabel: { fontSize: 11, color: colors.text.secondary },
  filter: { alignItems: 'center', gap: 6, marginHorizontal: 10, marginBottom: 8, paddingHorizontal: 10, borderRadius: 8, borderWidth: 1, borderColor: colors.border },
  filterInput: { flex: 1, paddingVertical: 6, fontSize: 12.5, color: colors.text.primary },
  tr: { alignItems: 'center', borderTopWidth: 1, borderTopColor: colors.border, minHeight: 38 },
  th: { backgroundColor: colors.background.secondary },
  thText: { fontSize: 11.5, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary },
  td: { paddingHorizontal: 10, paddingVertical: 7, justifyContent: 'center' },
  stripe: { backgroundColor: String(colors.background.secondary) + '80' },
  cellText: { fontSize: 12.5, lineHeight: 18, color: colors.text.primary },
  number: { fontVariant: ['tabular-nums'] },
  link: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  quickCol: { width: 70, gap: 2, paddingHorizontal: 6, alignItems: 'center' },
  quick: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primaryTransparent },
  chip: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: colors.background.secondary, maxWidth: '100%' },
  chip_good: { backgroundColor: String(colors.status.success) + '1c' },
  chip_warn: { backgroundColor: String(colors.status.warning) + '24' },
  chip_bad: { backgroundColor: String(colors.status.error) + '1c' },
  chip_neutral: {},
  chipText: { fontSize: 11.5, color: colors.text.secondary },
  chipText_good: { color: colors.status.success },
  chipText_warn: { color: colors.status.warning },
  chipText_bad: { color: colors.status.error },
  chipText_neutral: {},
  empty: { padding: 12, fontSize: 12, color: colors.text.secondary },
  foot: { alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 6, borderTopWidth: 1, borderTopColor: colors.border },
  footText: { fontSize: 11, color: colors.text.secondary },
  footButton: { paddingHorizontal: 8, paddingVertical: 5, borderRadius: 8 },
  more: { alignItems: 'center', gap: 4, backgroundColor: colors.primaryTransparent },
  footLink: { fontSize: 12, fontFamily: typography.fontFamily.semibold, color: colors.primary },
});
